// Roles and permissions: the matrix, IdP groups, roles given to users, re-keying and switching off.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { sv } from '@aldorock-fordon/shared/sv';
import type { Role } from '@aldorock-fordon/shared/permissions';
import { api, ApiError } from '../api.ts';
import { usePage } from '../page.ts';
import { PageHead } from '../ui/PageHead.tsx';
import { Table } from '../ui/Table.tsx';
import { ErrorSummary, SelectField, TextField } from '../ui/Fields.tsx';
import { Status } from '../ui/Status.tsx';
import { moment } from '../format.ts';

type Data = {
  roles: Array<{ role: Role; label: string }>;
  matrix: Array<{ action: string; label: string; roles: Role[] }>;
  mappings: Array<{ id: string; group: string; role: Role; unit: string | null }>;
  users: Array<{
    id: string;
    name: string;
    email: string | null;
    subject: string;
    status: string;
    department: string | null;
    lastLoginAt: string | null;
    roles: Array<{
      id: string;
      role: Role;
      unit: string | null;
      source: string;
      validTo: string | null;
    }>;
  }>;
  units: Array<{ id: string; name: string }>;
};

export function RolesPage() {
  const h1 = usePage(sv.rolesAdmin.title);
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const q = useQuery({
    queryKey: ['roles-admin', query],
    queryFn: () => api<Data>(`/admin/roles?q=${encodeURIComponent(query)}`),
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['roles-admin'] });
    void qc.invalidateQueries({ queryKey: ['me'] });
  };
  const [group, setGroup] = useState('');
  const [groupRole, setGroupRole] = useState('fordonsadministrator');
  const [groupUnit, setGroupUnit] = useState('');
  const [userId, setUserId] = useState('');
  const [userRole, setUserRole] = useState('fordonsadministrator');
  const [userUnit, setUserUnit] = useState('');
  const [validTo, setValidTo] = useState('');
  const [rekeyUser, setRekeyUser] = useState('');
  const [rekeySubject, setRekeySubject] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);
  const fail = (e: unknown) =>
    setErrors(
      e instanceof ApiError && e.errors.length
        ? Object.fromEntries(e.errors.map((x) => [x.path, x.message]))
        : { group: sv.errors.server },
    );
  const addGroup = useMutation({
    mutationFn: () =>
      api('/admin/group-mappings', {
        method: 'POST',
        body: JSON.stringify({ groupName: group, role: groupRole, orgUnitId: groupUnit || null }),
      }),
    onSuccess: () => {
      setGroup('');
      setMessage(sv.rolesAdmin.added);
      refresh();
    },
    onError: fail,
  });
  const removeGroup = useMutation({
    mutationFn: (id: string) => api(`/admin/group-mappings/${id}`, { method: 'DELETE' }),
    onSuccess: refresh,
  });
  const addRole = useMutation({
    mutationFn: () =>
      api(`/admin/users/${userId}/roles`, {
        method: 'POST',
        body: JSON.stringify({
          role: userRole,
          orgUnitId: userUnit || null,
          validTo: validTo || null,
        }),
      }),
    onSuccess: () => {
      setMessage(sv.rolesAdmin.added);
      refresh();
    },
    onError: fail,
  });
  const removeRole = useMutation({
    mutationFn: (p: { userId: string; roleId: string }) =>
      api(`/admin/users/${p.userId}/roles/${p.roleId}`, { method: 'DELETE' }),
    onSuccess: refresh,
  });
  const rekey = useMutation({
    mutationFn: () =>
      api(`/admin/users/${rekeyUser}/subject`, {
        method: 'POST',
        body: JSON.stringify({ subject: rekeySubject }),
      }),
    onSuccess: () => {
      setMessage(sv.rolesAdmin.rekeyed);
      setRekeySubject('');
      refresh();
    },
    onError: fail,
  });
  const status = useMutation({
    mutationFn: (p: { id: string; status: 'active' | 'disabled' }) =>
      api(`/admin/users/${p.id}/status`, {
        method: 'POST',
        body: JSON.stringify({ status: p.status }),
      }),
    onSuccess: refresh,
  });
  const d = q.data;
  const roleOptions = (d?.roles ?? []).map((r) => ({ value: r.role, label: r.label }));
  const unitOptions = [
    { value: '', label: sv.rolesAdmin.wholeOrg },
    ...(d?.units ?? []).map((u) => ({ value: u.id, label: u.name })),
  ];
  const userOptions = (d?.users ?? []).map((u) => ({
    value: u.id,
    label: `${u.name}${u.department ? ` (${u.department})` : ''}`,
  }));
  return (
    <>
      <PageHead
        help="roles"
        crumbs={[{ to: '/admin', label: sv.adminHome.title }, { label: sv.rolesAdmin.title }]}
        title={sv.rolesAdmin.title}
        titleRef={h1}
      />
      <p>{sv.rolesAdmin.intro}</p>
      {message ? (
        <p className="notice" data-state="done" role="status">
          {message}
        </p>
      ) : null}
      <ErrorSummary errors={Object.entries(errors).map(([k, m]) => ({ id: k, message: m }))} />
      {d ? (
        <>
          <Table caption={sv.rolesAdmin.matrix} hasOptional>
            <thead>
              <tr>
                <th scope="col">{sv.rolesAdmin.function}</th>
                {d.roles.map((r) => (
                  <th
                    scope="col"
                    key={r.role}
                    className={
                      r.role === 'bokare' || r.role === 'fordonsadministrator'
                        ? undefined
                        : 'col-opt'
                    }
                  >
                    {r.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {d.matrix.map((m) => (
                <tr key={m.action}>
                  <th scope="row" className="row-head">
                    {m.label}
                  </th>
                  {d.roles.map((r) => (
                    <td
                      key={r.role}
                      className={
                        r.role === 'bokare' || r.role === 'fordonsadministrator'
                          ? undefined
                          : 'col-opt'
                      }
                    >
                      {m.roles.includes(r.role) ? sv.yes : <span className="muted">{sv.no}</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>

          <Table caption={sv.rolesAdmin.groups} isEmpty={d.mappings.length === 0}>
            <thead>
              <tr>
                <th scope="col">{sv.rolesAdmin.group}</th>
                <th scope="col">{sv.rolesAdmin.role}</th>
                <th scope="col">{sv.rolesAdmin.unit}</th>
                <th scope="col">{sv.columns.action}</th>
              </tr>
            </thead>
            <tbody>
              {d.mappings.map((m) => (
                <tr key={m.id}>
                  <td>{m.group}</td>
                  <td>{sv.roles[m.role]}</td>
                  <td>{m.unit ?? sv.rolesAdmin.wholeOrg}</td>
                  <td>
                    <button
                      className="btn btn-quiet"
                      type="button"
                      aria-label={sv.rolesAdmin.removeGroup(m.group)}
                      onClick={() => removeGroup.mutate(m.id)}
                    >
                      {sv.rolesAdmin.removeShort}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <form
            className="form-block"
            aria-labelledby="add-group"
            onSubmit={(e) => {
              e.preventDefault();
              setErrors({});
              addGroup.mutate();
            }}
          >
            <h3 className="form-heading" id="add-group">
              {sv.rolesAdmin.addGroup}
            </h3>
            <TextField
              id="group"
              label={sv.rolesAdmin.group}
              value={group}
              onChange={setGroup}
              error={errors.groupName}
              required
            />
            <SelectField
              id="groupRole"
              label={sv.rolesAdmin.role}
              value={groupRole}
              options={roleOptions}
              onChange={setGroupRole}
            />
            <SelectField
              id="groupUnit"
              label={sv.rolesAdmin.unit}
              value={groupUnit}
              options={unitOptions}
              onChange={setGroupUnit}
            />
            <div className="form-actions">
              <button className="btn" type="submit" disabled={addGroup.isPending || !group.trim()}>
                {sv.rolesAdmin.addGroupButton}
              </button>
            </div>
          </form>

          <section className="block" aria-labelledby="users">
            <h2 className="block-title" id="users">
              {sv.rolesAdmin.users}
            </h2>
            <form
              className="filter-row"
              role="search"
              onSubmit={(e) => {
                e.preventDefault();
                setQuery(search.trim());
              }}
            >
              <TextField
                id="user-search"
                label={sv.rolesAdmin.searchUser}
                value={search}
                onChange={setSearch}
              />
              <div className="actions-inline">
                <button className="btn" type="submit">
                  {sv.vehicles.search}
                </button>
              </div>
            </form>
            <Table caption={sv.rolesAdmin.users} isEmpty={d.users.length === 0} hasOptional>
              <thead>
                <tr>
                  <th scope="col">{sv.rolesAdmin.user}</th>
                  <th scope="col">{sv.rolesAdmin.role}</th>
                  <th scope="col" className="col-opt">
                    {sv.rolesAdmin.lastLogin}
                  </th>
                  <th scope="col">{sv.columns.status}</th>
                </tr>
              </thead>
              <tbody>
                {d.users.map((u) => (
                  <tr key={u.id}>
                    <td>
                      {u.name}
                      <br />
                      <span className="caption">{u.department ?? u.email ?? ''}</span>
                    </td>
                    <td>
                      <ul className="plain">
                        {u.roles.map((r) => (
                          <li key={r.id}>
                            {sv.roles[r.role]}
                            {r.unit ? `, ${r.unit}` : ''}{' '}
                            <span className="muted">
                              ({sv.rolesAdmin.sources[r.source] ?? r.source}
                              {r.validTo
                                ? `, ${sv.rolesAdmin.validTo.toLowerCase()} ${r.validTo}`
                                : ''}
                              )
                            </span>
                            {r.source === 'manual' ? (
                              <button
                                className="btn btn-quiet"
                                type="button"
                                aria-label={sv.rolesAdmin.removeRole(sv.roles[r.role], u.name)}
                                onClick={() => removeRole.mutate({ userId: u.id, roleId: r.id })}
                              >
                                {sv.rolesAdmin.removeShort}
                              </button>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    </td>
                    <td className="col-opt num">{u.lastLoginAt ? moment(u.lastLoginAt) : ''}</td>
                    <td>
                      {u.status === 'active' ? (
                        <Status tone="done">{sv.rolesAdmin.active}</Status>
                      ) : (
                        <Status tone="none">{sv.rolesAdmin.disabled}</Status>
                      )}{' '}
                      <button
                        className="btn btn-quiet"
                        type="button"
                        aria-label={
                          u.status === 'active'
                            ? sv.rolesAdmin.disable(u.name)
                            : sv.rolesAdmin.enable(u.name)
                        }
                        onClick={() =>
                          status.mutate({
                            id: u.id,
                            status: u.status === 'active' ? 'disabled' : 'active',
                          })
                        }
                      >
                        {u.status === 'active'
                          ? sv.rolesAdmin.disable('').trim()
                          : sv.rolesAdmin.enable('').trim()}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <form
              className="form-block"
              aria-labelledby="add-role"
              onSubmit={(e) => {
                e.preventDefault();
                setErrors({});
                addRole.mutate();
              }}
            >
              <h3 className="form-heading" id="add-role">
                {sv.rolesAdmin.addRole}
              </h3>
              <SelectField
                id="userId"
                label={sv.rolesAdmin.user}
                value={userId}
                options={userOptions}
                onChange={setUserId}
              />
              <SelectField
                id="userRole"
                label={sv.rolesAdmin.role}
                value={userRole}
                options={roleOptions}
                onChange={setUserRole}
              />
              <SelectField
                id="userUnit"
                label={sv.rolesAdmin.unit}
                value={userUnit}
                options={unitOptions}
                onChange={setUserUnit}
              />
              <TextField
                id="validTo"
                label={sv.rolesAdmin.validTo}
                help={sv.rolesAdmin.validToHelp}
                type="date"
                value={validTo}
                onChange={setValidTo}
              />
              <div className="form-actions">
                <button className="btn" type="submit" disabled={addRole.isPending || !userId}>
                  {sv.rolesAdmin.addRoleButton}
                </button>
              </div>
            </form>
            <form
              className="form-block"
              aria-labelledby="rekey"
              onSubmit={(e) => {
                e.preventDefault();
                setErrors({});
                rekey.mutate();
              }}
            >
              <h3 className="form-heading" id="rekey">
                {sv.rolesAdmin.rekey}
              </h3>
              <p className="caption">{sv.rolesAdmin.rekeyHelp}</p>
              <SelectField
                id="rekeyUser"
                label={sv.rolesAdmin.user}
                value={rekeyUser}
                options={userOptions}
                onChange={setRekeyUser}
              />
              <TextField
                id="rekeySubject"
                label={sv.rolesAdmin.rekeyLabel}
                value={rekeySubject}
                onChange={setRekeySubject}
              />
              <div className="form-actions">
                <button
                  className="btn"
                  type="submit"
                  disabled={rekey.isPending || !rekeyUser || !rekeySubject.trim()}
                >
                  {sv.rolesAdmin.rekeyButton}
                </button>
              </div>
            </form>
          </section>
        </>
      ) : (
        <p role="status">{sv.app.loading}</p>
      )}
    </>
  );
}
